import { apiClient } from './client';
import type {
  AccessoryLink, StockAccessoryMapping,
  QuoteRequest, QuoteRequestDetail, QuoteRequestWrite, QuoteWrite,
  StockComponentResponse, StockComponentWriteItem,
  StockDeviceMapping, StockItem, StockItemDetail, StockItemWrite, StockTransactionWrite,
} from '@/types/oscar';

export interface StockQuery {
  itemType?: string;
  search?: string;
  lowStockOnly?: boolean;
}

function qs(q: StockQuery): string {
  const p = new URLSearchParams();
  if (q.itemType) p.set('itemType', q.itemType);
  if (q.search) p.set('search', q.search);
  if (q.lowStockOnly) p.set('lowStockOnly', 'true');
  const s = p.toString();
  return s ? `?${s}` : '';
}

export function listStockItems(query: StockQuery = {}, signal?: AbortSignal): Promise<StockItem[]> {
  return apiClient.get<StockItem[]>(`/api/stock-items${qs(query)}`, signal);
}
export function getStockItem(id: number, signal?: AbortSignal): Promise<StockItemDetail> {
  return apiClient.get<StockItemDetail>(`/api/stock-items/${id}`, signal);
}
export function createStockItem(body: StockItemWrite): Promise<StockItem> {
  return apiClient.post<StockItem>('/api/stock-items', body);
}
/**
 * POST /api/hardware-items - add ONE item everywhere it has to exist: the catalogue option a requester
 * can ask for, the stock line the warehouse counts, and the mapping between them, in one transaction
 * (admin). Stock/catalogue consolidation, PR 2.
 *
 * ⛔ ALL OR NOTHING: a failure adds none of it. ⛔ ADDS ONLY - a name already in the catalogue is
 * refused, never merged. One name and one price are written to both records.
 */
export function addHardwareItem(body: AddHardwareItemBody): Promise<AddHardwareItemResult> {
  return apiClient.post<AddHardwareItemResult>('/api/hardware-items', body);
}

export interface AddHardwareItemBody {
  /** A consumable is not asked for on a request, so it is not added this way. */
  Kind: 'Device' | 'Accessory';
  Name: string;
  Category?: string | null;
  Make?: string | null;
  Model?: string | null;
  LocationId: number;
  InitialQty?: number;
  /** A device's goes on the catalogue option (all warehouses); an accessory's on the stock line. */
  MinQty?: number | null;
  UnitCost?: number | null;
  IsSerialised?: boolean;
  SupplierPartNumber?: string | null;
  Description?: string | null;
  Notes?: string | null;
  /** An accessory only: the devices it is offered for (the 'no device needed' option counts). */
  DeviceOptionIds?: number[];
  /** An accessory only: the devices it is REQUIRED for. Each must also be in DeviceOptionIds. */
  RequiredDeviceOptionIds?: number[];
}

export interface AddHardwareItemResult {
  StockItemId: number;
  OptionKind: 'Device' | 'Accessory';
  /** The DeviceOptionId or AccessoryOptionId, per OptionKind. */
  OptionId: number;
}

export function updateStockItem(id: number, body: StockItemWrite): Promise<StockItem> {
  return apiClient.patch<StockItem>(`/api/stock-items/${id}`, body);
}
export function recordStockTransaction(id: number, body: StockTransactionWrite): Promise<StockItem> {
  return apiClient.post<StockItem>(`/api/stock-items/${id}/transactions`, body);
}
export function deleteStockItem(id: number): Promise<unknown> {
  return apiClient.post(`/api/stock-items/${id}/delete`);
}

/**
 * GET /api/stock-items/{id}/devices — which hardware-request devices this line
 * satisfies, and every option it could (076).
 */
export function getStockItemDevices(id: number, signal?: AbortSignal): Promise<StockDeviceMapping> {
  return apiClient.get<StockDeviceMapping>(`/api/stock-items/${id}/devices`, signal);
}

/**
 * PUT /api/stock-items/{id}/devices — replace that set (admin).
 *
 * ⛔ REPLACED, not merged: the screen sends the complete set it is showing, so
 * a device the administrator unticked must disappear.
 */
export function putStockItemDevices(id: number, deviceOptionIds: number[]): Promise<StockDeviceMapping> {
  return apiClient.put<StockDeviceMapping>(`/api/stock-items/${id}/devices`, {
    DeviceOptionIds: deviceOptionIds,
  });
}

/**
 * GET /api/stock-items/{id}/components — what this line is made of (106).
 *
 * ⚠ Reads the resolved answer. A part that points at another stock line takes
 * that line's name and part number LIVE, so correcting a cable's number once
 * corrects every kit containing one.
 */
export function getStockItemComponents(id: number, signal?: AbortSignal): Promise<StockComponentResponse> {
  return apiClient.get<StockComponentResponse>(`/api/stock-items/${id}/components`, signal);
}

/**
 * PUT /api/stock-items/{id}/components — replace the parts list.
 *
 * ⛔ REPLACED, not merged: the editor sends the complete list it is showing,
 * so a part somebody removed has to disappear. The ORDER of the array is the
 * order the parts are stored in — the server numbers them by position and
 * ignores any order the body tries to carry.
 */
export function putStockItemComponents(
  id: number,
  components: StockComponentWriteItem[],
): Promise<StockComponentResponse> {
  return apiClient.put<StockComponentResponse>(`/api/stock-items/${id}/components`, { Components: components });
}

// ── quote requests, and the answers that come back (107) ────────────────────

export function listQuoteRequests(signal?: AbortSignal): Promise<{ Requests: QuoteRequest[] }> {
  return apiClient.get<{ Requests: QuoteRequest[] }>('/api/stock-quote-requests', signal);
}

export function getQuoteRequest(id: number, signal?: AbortSignal): Promise<QuoteRequestDetail> {
  return apiClient.get<QuoteRequestDetail>(`/api/stock-quote-requests/${id}`, signal);
}

/**
 * ⛔ The SPEC IS NOT SENT. The server snapshots it from the stock lines, so a
 * client cannot record a specification nobody was ever shown.
 */
export function createQuoteRequest(body: QuoteRequestWrite): Promise<{ StockQuoteRequestId: number }> {
  return apiClient.post<{ StockQuoteRequestId: number }>('/api/stock-quote-requests', body);
}

export function createQuote(requestId: number, body: QuoteWrite): Promise<{ StockQuoteId: number }> {
  return apiClient.post<{ StockQuoteId: number }>(`/api/stock-quote-requests/${requestId}/quotes`, body);
}

/** ⚠ Accepting another switches — the server stands the previous one down. */
export function acceptQuote(quoteId: number, accepted: boolean): Promise<unknown> {
  return apiClient.post(`/api/stock-quotes/${quoteId}/accept`, { Accepted: accepted });
}

// ── Accessories (oscar migration 124) ────────────────────────────────────────

/**
 * GET /api/stock-items/accessory-links - every live (stock line, accessory) pair,
 * in ONE read.
 *
 * ⚠ One read, not one per line: the catalogue shows what answers each accessory
 * and the Stock page shows what each line answers, and fetching the mapping per
 * line would be a request per row. The caller groups the pairs.
 */
export function listAccessoryLinks(signal?: AbortSignal): Promise<AccessoryLink[]> {
  return apiClient.get<AccessoryLink[]>('/api/stock-items/accessory-links', signal);
}

/**
 * GET /api/stock-items/{id}/accessories - which hardware-request accessories this
 * line satisfies, and every option it could (migration 124).
 */
export function getStockItemAccessories(id: number, signal?: AbortSignal): Promise<StockAccessoryMapping> {
  return apiClient.get<StockAccessoryMapping>(`/api/stock-items/${id}/accessories`, signal);
}

/**
 * PUT /api/stock-items/{id}/accessories - replace that set (admin).
 *
 * ⛔ REPLACED, not merged: the screen sends the complete set it is showing, so an
 * accessory the administrator unticked must disappear.
 *
 * ⛔ An ACCESSORY line only. A device line answers devices and a consumable is
 * used up, so the server refuses either with a sentence saying which.
 */
export function putStockItemAccessories(id: number, accessoryOptionIds: number[]): Promise<StockAccessoryMapping> {
  return apiClient.put<StockAccessoryMapping>(`/api/stock-items/${id}/accessories`, {
    AccessoryOptionIds: accessoryOptionIds,
  });
}
